/**
 * WorkOrdersPage - Work order management page using the shared JobListTemplate.
 *
 * Configures the JobListTemplate with work order-specific stat cards, columns,
 * filters, and API fetch logic. The layout, pagination, skeleton loading, and
 * filter panel UI are handled entirely by the template.
 *
 * Features:
 *	 - Stat cards: Open Orders, Avg Turnaround, High Priority, Billable Volume
 *	 - Per-row summary band: Subject (projectName) + Location, mirroring
 *	   the Building/Customer band on the Inspections list
 *	 - Table columns: Order ID, Status, Priority, Assignee, Date, Actions
 *	 - Assign from the row: for anyone with the Can Reassign Work Orders
 *	   permission the Assignee cell is a button (and the kebab carries
 *	   Assign / Reassign) that opens the AssignWorkOrderModal — the form's
 *	   Assignment section in a dialog — so a crew can be put on a work order
 *	   without opening the whole form
 *	 - Filters: Status (checkboxes), Priority (dropdown), Date Range, Business Line (chips), Billing Type (tags)
 *
 * Data source: GET /api/work-orders with search, filter, and pagination params.
 *
 * Accessible by: all authenticated users.
 * Role-specific: managers/admins/developers can create new work orders.
 *
 * @route /work-orders
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../hooks/useAuth';
import { useDeleteJob } from '../../hooks/useDeleteJob';
import { workOrderService } from '../../services/workOrderService';
import type { WorkOrder, PaginatedResponse } from '../../services/workOrderService';
import { normalizeWorkOrder } from '../../services/workOrderService';
import { camelizeKeys } from '../../services/api';
import { qk } from '../../lib/queryKeys';
import { invalidateJobCaches } from '../../lib/invalidateJobCaches';
import { isoDaysAgo } from '../../utils/formatDate';
import { formatStatusLabel } from '../../utils/formatLabel';
import { usePersistedFilterState } from '../../hooks/usePersistedFilterState';
import { JobListTemplate } from '../../components/templates/JobListTemplate';
import type { StatCardConfig, ColumnConfig, FilterControlConfig } from '../../components/templates/JobListTemplate';
import { ActionMenu, RecurrenceBadge, DateTimeStack } from '../../components/atoms';
import { JobStatusActionButton, JobProgressIndicator, AssigneeCell } from '../../components/molecules';
import { AssignWorkOrderModal } from '../../components/organisms/AssignWorkOrderModal';
import { generateWorkOrderListPdf } from '../../utils/pdfGenerator';
import type { ListFilterSummary } from '../../utils/pdfGenerator';
import type { BillingInfoTag } from '../../utils/constants';
import { WORK_ORDER_CREATE_ROLES } from '../../utils/constants';

/* ============================================
 * PRIORITY DOT + LABEL COLOR MAPS
 * Small colored dots paired with text labels for priority levels.
 * ============================================ */
const PRIORITY_DOT_COLORS: Record<string, string> = {
	urgent: 'bg-red-500',
	high: 'bg-orange-500',
	medium: 'bg-yellow-400',
	low: 'bg-blue-400',
};

const PRIORITY_LABEL_COLORS: Record<string, string> = {
	urgent: 'text-red-700',
	high: 'text-orange-700',
	medium: 'text-yellow-700',
	low: 'text-blue-600',
};

const PRIORITY_LABELS: Record<string, string> = {
	urgent: 'CRITICAL',
	high: 'HIGH',
	medium: 'MEDIUM',
	low: 'LOW',
};

/* ============================================
 * FILTER OPTIONS
 * Used by the filter panel via FilterControlConfig.
 * ============================================ */

/** Status options for the checkbox filter group, in lifecycle order
 *  (pending → in_progress → in_review → approved → completed, then the
 *  post-completion billing tail). Invoiced/Closed are listed so the
 *  dashboard's "Completed (this period)" deep link — which includes the
 *  billing-tail statuses — never applies a filter the panel can't show. */
const STATUS_FILTER_OPTIONS = [
	{ value: 'pending', label: 'Pending' },
	{ value: 'in_progress', label: 'In Progress' },
	{ value: 'in_review', label: 'In Review' },
	{ value: 'approved', label: 'Approved' },
	{ value: 'completed', label: 'Completed' },
	{ value: 'invoiced', label: 'Invoiced' },
	{ value: 'closed', label: 'Closed' },
	{ value: 'cancelled', label: 'Cancelled' },
];

/** Priority options for the dropdown filter */
const PRIORITY_FILTER_OPTIONS = [
	{ value: '', label: 'All Priorities' },
	{ value: 'low', label: 'Low' },
	{ value: 'medium', label: 'Medium' },
	{ value: 'high', label: 'High' },
	{ value: 'urgent', label: 'Urgent' },
];

/** Business line chip options */
const BUSINESS_LINE_CHIPS = [
	{ value: 'janitorial', label: 'Janitorial' },
	{ value: 'construction', label: 'Construction' },
];

/** Billing type tag options.
 *
 *  The values are billing-classification tags from the shared BILLING_INFO_TAGS
 *  list — i.e. what the job forms write into the work order's billing_info
 *  array and what the server matches the ?billing_type= param against. They
 *  previously carried billing_type enum slugs ('fixed_price', ...), a column
 *  nothing populates, so every selection returned an empty list.
 *
 *  Only the three tags operators filter by are surfaced here; the rest of
 *  BILLING_INFO_TAGS (PW, Davis Bacon, ...) stay form-only. Each value is
 *  drawn from the shared list so a rename there breaks the build rather than
 *  silently emptying the filter again. */
const BILLING_TYPE_TAGS: { value: BillingInfoTag; label: string }[] = [
	{ value: 'Billable', label: 'Billable' },
	{ value: 'T&M', label: 'T&M' },
	{ value: 'Non Billable', label: 'Non-Billable' },
];

/** Billability chip options — Super Admin only.
 *
 *  The orthogonal dimension the list was missing. Order Status already covers
 *  the billing tail (Invoiced / Closed are checkboxes in that group), and
 *  Billing Type matches a single classification tag, but neither answers "is
 *  this job billable at all?" — which is the question that turns the list into
 *  an invoicing worklist when combined with Status = Completed.
 *
 *  Maps to ?billable=, which the server defines by ABSENCE of the 'Non
 *  Billable' tag rather than presence of a positive one: a job may be tagged
 *  'Billable', 'T&M', both, or nothing, and an untagged job still counts as
 *  billable. That is why this is a separate control from Billing Type rather
 *  than two more entries in it. */
const BILLABILITY_CHIPS = [
	{ value: '1', label: 'Billable' },
	{ value: '0', label: 'Non-Billable' },
];

/* ============================================
 * FILTER DEFAULTS
 * The Filter Control opens pre-narrowed to the work most operators want
 * first: active orders (In Progress / Pending) over the last two weeks.
 * A signed-in user can change these and their choices persist in
 * localStorage until they sign out (see usePersistedFilterState).
 * ============================================ */

/** Default status selection — active orders only. */
const DEFAULT_STATUS_FILTER = ['in_progress', 'pending'];
/** Default date-range start — look back two weeks from today. */
const DEFAULT_DATE_FROM = isoDaysAgo(14);

/* Snake_case → Title Case formatting is shared via utils/formatLabel.
   The inline helper this page used to carry was identical. */

/* ============================================
 * MAIN COMPONENT
 * ============================================ */
const WorkOrdersPage: React.FC = () => {
	const navigate = useNavigate();
	const { user, hasAnyRole } = useAuth();
	const queryClient = useQueryClient();

	/* ---- Pagination (page is UI state; totals come from the query) ---- */
	const [currentPage, setCurrentPage] = useState(1);

	/* ---- Deep-link filters ----
	 * The dashboard stat cards link here with ?status=…&date_from=…&date_to=….
	 * When any of those params is present, the URL defines the COMPLETE filter
	 * state — unspecified dimensions reset to cleared (not to the persisted
	 * copy or the 2-week default) so the landing list matches the card's
	 * number. The values flow through the persisted-state overrides below,
	 * then the params are stripped from the URL (one-shot semantics). */
	const [searchParams, setSearchParams] = useSearchParams();
	const hasDeepLink = searchParams.has('status') || searchParams.has('date_from') || searchParams.has('date_to');
	const deepLink = hasDeepLink
		? {
			statuses: (searchParams.get('status') ?? '').split(',').filter(Boolean),
			dateFrom: searchParams.get('date_from') ?? '',
			dateTo: searchParams.get('date_to') ?? '',
		}
		: undefined;

	useEffect(() => {
		if (hasDeepLink) setSearchParams({}, { replace: true });
	}, [hasDeepLink, setSearchParams]);

	/* ---- Filter state ----
	 * Persisted to localStorage per-field so a signed-in user's Filter Control
	 * selections survive reloads/navigation; the defaults (active statuses +
	 * 2-week look-back) apply on a fresh session and after sign-out. */
	const [filterStatuses, setFilterStatuses] = usePersistedFilterState<string[]>('work-orders.statuses', DEFAULT_STATUS_FILTER, deepLink?.statuses);
	const [filterPriority, setFilterPriority] = usePersistedFilterState('work-orders.priority', '', deepLink ? '' : undefined);
	const [filterDateFrom, setFilterDateFrom] = usePersistedFilterState('work-orders.dateFrom', DEFAULT_DATE_FROM, deepLink?.dateFrom);
	const [filterDateTo, setFilterDateTo] = usePersistedFilterState('work-orders.dateTo', '', deepLink?.dateTo);
	const [filterBusinessLine, setFilterBusinessLine] = usePersistedFilterState('work-orders.businessLine', '', deepLink ? '' : undefined);
	/* Persisted under a new key ('.billingTag' rather than '.billingType') so a
	   selection saved against the retired enum slugs can't hydrate into the tag
	   options above and silently pin the list to zero results. */
	const [filterBillingType, setFilterBillingType] = usePersistedFilterState('work-orders.billingTag', '', deepLink ? '' : undefined);
	/* Billability — Super Admin only, so it is persisted under its own key and
	   read back out of the params below only when the control is shown. */
	const [filterBillable, setFilterBillable] = usePersistedFilterState('work-orders.billable', '', deepLink ? '' : undefined);

	/* New Order + Duplicate — technicians as well as the manager tier
	   (WORK_ORDER_CREATE_ROLES). */
	const canCreate = hasAnyRole([...WORK_ORDER_CREATE_ROLES]);
	/* Edit + Delete from the list stay with managers, admins, super_admins
	   and developers — a technician can book new work but not rewrite or
	   remove someone else's. */
	const canManage = hasAnyRole(['super_admin', 'admin', 'manager', 'developer']);
	/* Row Delete — confirms, soft-deletes and refreshes the list (shared
	   with the detail page via useDeleteJob). */
	const { requestDelete, deleteModal } = useDeleteJob('work_order');
	/* Who may assign and reassign from the list is a per-person permission,
	   not a role: the "Can Reassign Work Orders" switch on Admin → Users
	   (users.can_reassign). It follows the same pattern as Can View
	   Financials, which alone decides whether the detail page shows Billing.
	   The seed data shows the intent — our own admins, manager and developer
	   have it, a customer company's manager and the technicians don't.
	   Everyone without it sees the plain assignee text.

	   The server enforces the same permission on the move itself, and the
	   edit form and the field workflow lock the primary assignee without it. */
	const canAssign = Boolean(user?.canReassign);

	/* The row whose crew is being edited in the AssignWorkOrderModal, or
	   null while it is closed. Page-local UI state, like the filters. */
	const [assignTarget, setAssignTarget] = useState<WorkOrder | null>(null);
	/* Billability is an invoicing concern, and invoicing is Super Admin
	   territory (see utils/statusTransitions and the backend's billing-tail
	   gate). Everyone else gets the list exactly as before. */
	const canSeeBilling = hasAnyRole(['super_admin']);

	/* PDF list-export state — flips while jspdf renders the doc so we can
	 * disable the button instead of letting the user double-trigger it. */
	const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

	/* Build query params from active filters + page. Memoized so the query key
	 * is stable across renders (and so changing a filter produces a new key,
	 * which TanStack fetches + caches automatically). */
	const params = useMemo<Record<string, string>>(() => {
		const p: Record<string, string> = { page: String(currentPage) };
		if (filterStatuses.length > 0) p.status = filterStatuses.join(',');
		if (filterPriority) p.priority = filterPriority;
		if (filterDateFrom) p.date_from = filterDateFrom;
		if (filterDateTo) p.date_to = filterDateTo;
		if (filterBusinessLine) p.business_line = filterBusinessLine;
		if (filterBillingType) p.billing_type = filterBillingType;
		/* Gated on the role as well as the value: a super admin who sets this
		   and is later demoted must not keep an invisible filter pinned to
		   their list. */
		if (canSeeBilling && filterBillable) p.billable = filterBillable;
		return p;
	}, [currentPage, filterStatuses, filterPriority, filterDateFrom, filterDateTo, filterBusinessLine, filterBillingType, canSeeBilling, filterBillable]);

	/* ---- Work orders read (TanStack Query) ----
	 * The queryFn carries the same both-shapes handling + camelize/normalize the
	 * page used before, and returns the list plus pagination meta as one object. */
	const query = useQuery({
		queryKey: qk.workOrders.list(params),
		queryFn: async () => {
			const response = await workOrderService.getAll(params);
			const data = response.data as PaginatedResponse<WorkOrder>;
			if (data && Array.isArray(data.data)) {
				const items = (camelizeKeys(data.data) as Record<string, unknown>[]).map(normalizeWorkOrder);
				return { items, totalPages: data.last_page || 1, totalItems: data.total || data.data.length, perPage: data.per_page || 25 };
			}
			if (Array.isArray(response.data)) {
				const items = (camelizeKeys(response.data) as Record<string, unknown>[]).map(normalizeWorkOrder);
				return { items, totalPages: 1, totalItems: items.length, perPage: 25 };
			}
			return { items: [] as WorkOrder[], totalPages: 1, totalItems: 0, perPage: 25 };
		},
	});

	const workOrders: WorkOrder[] = useMemo(() => query.data?.items ?? [], [query.data]);
	const totalPages = query.data?.totalPages ?? 1;
	const totalItems = query.data?.totalItems ?? 0;
	const perPage = query.data?.perPage ?? 25;
	const isLoading = query.isLoading;
	const error = query.error ? (query.error as Error).message : null;

	/* Stat card values derived from the current page's list. "Open" means
	 * any non-terminal lifecycle state — not yet completed/invoiced/closed
	 * and not cancelled. */
	const openOrdersCount = useMemo(
		() => workOrders.filter((wo) => ['pending', 'in_progress', 'on_hold', 'in_review', 'approved'].includes(wo.status)).length,
		[workOrders]
	);
	const highPriorityCount = useMemo(
		() => workOrders.filter((wo) => ['high', 'urgent'].includes(wo.priority)).length,
		[workOrders]
	);

	/* Reset to page 1 when filters change */
	useEffect(() => {
		setCurrentPage(1);
	}, [filterStatuses, filterPriority, filterDateFrom, filterDateTo, filterBusinessLine, filterBillingType, filterBillable]);

	/**
	 * Transition a work order from a pre-start status into 'in_progress'.
	 * Wired up to the row-level Start CTA. After the PATCH succeeds the caches
	 * are invalidated so the row's status badge + CTA reflect the new state,
	 * and the caller navigates to the detail page to begin work. Starting a
	 * job also moves it between Dashboard status buckets, so the Dashboard
	 * reads go with it — see invalidateJobCaches.
	 */
	const startWorkOrderMutation = useMutation({
		mutationFn: (id: string | number) => workOrderService.update(String(id), { status: 'in_progress' }),
		onSuccess: () => {
			invalidateJobCaches(queryClient, 'work_order');
		},
		onError: (err) => {
			console.error('[WorkOrdersPage] Failed to start work order:', err);
		},
	});
	const handleStartWorkOrder = (id: string | number) => startWorkOrderMutation.mutate(id);

	/* ============================================
	 * STAT CARD CONFIGURATION
	 * ============================================ */

	const stats: StatCardConfig[] = [
		{ label: 'OPEN ORDERS', value: openOrdersCount, subtitle: 'Active this week', isAccent: true },
		{ label: 'AVG TURNAROUND', value: '--', subtitle: 'Maintaining SLA targets', valueColor: 'text-brand-accent' },
		{ label: 'HIGH PRIORITY', value: highPriorityCount, subtitle: 'Action required', valueColor: 'text-red-600', subtitleColor: 'text-red-500' },
		{ label: 'BILLABLE VOLUME', value: '--', subtitle: 'Q3 Projection: On Track', valueColor: 'text-green-600' },
	];

	/* ============================================
	 * TABLE COLUMN CONFIGURATION
	 * ============================================ */

	const columns: ColumnConfig[] = [
		{
			/* Date leads the row — stacked DAY / MM/DD/YY / time (EST) via the
			   shared DateTimeStack atom. Falls back to createdAt when a work
			   order has no scheduled date yet. */
			key: 'date',
			header: 'Date',
			render: (wo: WorkOrder) => (
				<DateTimeStack date={wo.scheduledDate || wo.createdAt} className="text-12px" />
			),
		},
		{
			key: 'id',
			header: 'Order ID',
			render: (wo: WorkOrder) => (
				<button type="button" onClick={() => navigate(`/work-orders/${wo.id}`)} className="text-14px font-semibold text-brand-accent hover:underline">WO-{String(wo.id).slice(0, 8)}</button>
			),
		},
		{
			key: 'status',
			header: 'Status',
			/* Completed jobs collapse to a check icon; everything else
			   renders a progress bar with the status label + %. The
			   status → percent mapping lives in utils/jobProgress so
			   the detail pages can render the same thing. */
			render: (wo: WorkOrder) => (
				<JobProgressIndicator status={wo.status} type="work_order" />
			),
		},
		{
			key: 'priority',
			header: 'Priority',
			render: (wo: WorkOrder) => (
				<div className="flex items-center gap-6px">
					<span className={['w-8px h-8px rounded-full flex-shrink-0', PRIORITY_DOT_COLORS[wo.priority] || 'bg-gray-400'].join(' ')} />
					<span className={['text-12px font-semibold uppercase', PRIORITY_LABEL_COLORS[wo.priority] || 'text-gray-600'].join(' ')}>{PRIORITY_LABELS[wo.priority] || 'MEDIUM'}</span>
				</div>
			),
		},
		{
			key: 'assignee',
			header: 'Assignee',
			/* Who's on the job — and, for the roles that dispatch, the button
			   that opens the assignment dialog. An unassigned row shows a
			   dashed "Assign" call to action; an assigned one a quiet pill
			   that reassigns. Without the handler it is plain text. */
			render: (wo: WorkOrder) => (
				<AssigneeCell
					assigneeName={wo.assigneeName}
					recordLabel={`work order ${wo.id}`}
					onAssign={canAssign ? () => setAssignTarget(wo) : undefined}
				/>
			),
		},
		{
			/* Primary status-driven CTA — Start when the work order
			   hasn't been started, Resume when it's in flight, View when
			   terminal. Start + Resume deep-link into ?workflow=1 so the
			   detail page auto-opens the WorkOrderWorkflow overlay
			   instead of stopping on the read-only details tab. */
			key: 'primaryAction',
			header: '',
			render: (wo: WorkOrder) => (
				<JobStatusActionButton
					status={wo.status}
					ariaLabel={`${wo.status} action for work order ${wo.id}`}
					onStart={() => handleStartWorkOrder(wo.id)}
					onActive={() => navigate(`/work-orders/${wo.id}?workflow=1`)}
					onView={() => navigate(`/work-orders/${wo.id}`)}
				/>
			),
		},
		{
			key: 'actions',
			header: '',
			widthClass: 'w-48px',
			render: (wo: WorkOrder) => (
				<ActionMenu
					ariaLabel={`Actions for work order ${wo.id}`}
					items={[
						{ label: 'View', onClick: () => navigate(`/work-orders/${wo.id}`) },
						/* Same dialog the Assignee cell opens, listed here too so
						   the menu stays the complete list of row actions. */
						canAssign && { label: wo.assigneeName ? 'Reassign' : 'Assign', onClick: () => setAssignTarget(wo) },
						canManage && { label: 'Edit', onClick: () => navigate(`/work-orders/${wo.id}/edit`) },
						canCreate && { label: 'Duplicate', onClick: () => navigate(`/work-orders/create?duplicate=${wo.id}`) },
						canManage && { label: 'Delete', variant: 'danger', onClick: () => requestDelete(wo.id, wo.projectName) },
					]}
				/>
			),
		},
	];

	/* ============================================
	 * FILTER CONFIGURATION
	 * ============================================ */

	const filters: FilterControlConfig[] = [
		{ key: 'status', label: 'Order Status', type: 'checkbox_group', options: STATUS_FILTER_OPTIONS, value: filterStatuses, onChange: setFilterStatuses },
		{ key: 'priority', label: 'Priority Level', type: 'select', options: PRIORITY_FILTER_OPTIONS, value: filterPriority, onChange: setFilterPriority },
		{ key: 'dateRange', label: 'Date Range', type: 'date_range', value: [filterDateFrom, filterDateTo], onChange: (val: string[]) => { setFilterDateFrom(val[0] || ''); setFilterDateTo(val[1] || ''); } },
		{ key: 'businessLine', label: 'Business Line', type: 'chips', options: BUSINESS_LINE_CHIPS, value: filterBusinessLine, onChange: setFilterBusinessLine },
		{ key: 'billingType', label: 'Billing Type', type: 'tags', options: BILLING_TYPE_TAGS, value: filterBillingType, onChange: setFilterBillingType },
		/* Spread so the control simply isn't in the panel for other roles,
		   rather than being rendered disabled — there is nothing they could do
		   with it. */
		...(canSeeBilling
			? [{ key: 'billable', label: 'Billability', type: 'chips' as const, options: BILLABILITY_CHIPS, value: filterBillable, onChange: setFilterBillable }]
			: []),
	];

	/** Resets all filter controls back to their default state — active
	 *  statuses + the 2-week look-back window, with the rest cleared. */
	const resetFilters = () => {
		setFilterStatuses(DEFAULT_STATUS_FILTER);
		setFilterPriority('');
		setFilterDateFrom(DEFAULT_DATE_FROM);
		setFilterDateTo('');
		setFilterBusinessLine('');
		setFilterBillingType('');
		setFilterBillable('');
	};

	/**
	 * Generates and downloads a summary PDF of the currently visible work
	 * order rows (one page worth — the report mirrors what the user is
	 * looking at, not the full multi-page set). The active filters are
	 * surfaced in the report header so the printed copy stays
	 * self-explanatory.
	 */
	const handleGeneratePdf = () => {
		setIsGeneratingPdf(true);
		try {
			const filters: ListFilterSummary[] = [
				{ label: 'Status', value: filterStatuses.length > 0 ? filterStatuses.map(formatStatusLabel).join(', ') : '—' },
				{ label: 'Priority', value: filterPriority ? formatStatusLabel(filterPriority) : '—' },
				{ label: 'Date Range', value: filterDateFrom || filterDateTo ? `${filterDateFrom || '—'} – ${filterDateTo || '—'}` : '—' },
				{ label: 'Business Line', value: filterBusinessLine ? formatStatusLabel(filterBusinessLine) : '—' },
				{ label: 'Billing Type', value: BILLING_TYPE_TAGS.find((t) => t.value === filterBillingType)?.label || '—' },
				/* Only listed when the control was available — a printed report
				   should not name a filter its reader has no way to set. */
				...(canSeeBilling
					? [{ label: 'Billability', value: BILLABILITY_CHIPS.find((b) => b.value === filterBillable)?.label || '—' }]
					: []),
			];
			generateWorkOrderListPdf(workOrders, filters);
		} catch (err) {
			alert(err instanceof Error ? err.message : 'Failed to generate PDF');
		} finally {
			setIsGeneratingPdf(false);
		}
	};

	/* ============================================
	 * RENDER -- Delegate to JobListTemplate
	 * ============================================ */

	return (
		<>
			<JobListTemplate
				className="work-orders-page"
				dataRef="page-work-orders-page"
				title="Work Orders"
				subtitle="Managing field operations and task deployments."
				createLabel="New Order"
				createRoute="/work-orders/create"
				canCreate={canCreate}
				secondaryActionLabel={isGeneratingPdf ? 'Generating…' : 'Generate PDF'}
				onSecondaryActionClick={handleGeneratePdf}
				secondaryActionDisabled={isGeneratingPdf || workOrders.length === 0}
				stats={stats}
				columns={columns}
				filters={filters}
				items={workOrders}
				isLoading={isLoading}
				error={error}
				currentPage={currentPage}
				totalPages={totalPages}
				totalItems={totalItems}
				perPage={perPage}
				onPageChange={setCurrentPage}
				onRetry={() => query.refetch()}
				onResetFilters={resetFilters}
				emptyMessage={canCreate ? 'No work orders found. Create your first work order to get started.' : 'No work orders found. Check back later for assigned work orders.'}
				/* Surface the Subject + Location pair as a full-width summary
				   band above each row, mirroring the Building + Customer band
				   on the Inspections list. The columned row below carries the
				   operational fields (ID, Status, Priority, Assignee, Date,
				   CTAs); the band gives ops the "what work / where" context
				   at a glance without spending a column on it. */
				renderRowHeader={(wo: WorkOrder) => (
					<div className="flex items-center gap-12px flex-wrap">
						<div className="flex items-center gap-6px min-w-0">
							<svg className="w-14px h-14px text-brand-text-muted flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
								<path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
							</svg>
							<span className="text-13px font-semibold text-brand-text truncate">{wo.projectName}</span>
						</div>
						<span className="text-12px text-brand-text-muted">·</span>
						<div className="flex items-center gap-4px min-w-0">
							<svg className="w-12px h-12px text-brand-text-muted flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
								<path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
								<path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" />
							</svg>
							<span className="text-12px text-brand-text-muted truncate">{wo.locationName || wo.projectAddress || '—'}</span>
						</div>
						{/* RecurrenceBadge — surfaces only when this WO is part of a
						    series, matching the spec's "way to identify the Repeats". */}
						{wo.recurrenceSeriesId && <RecurrenceBadge />}
					</div>
				)}
			/>

			{/* Assign / reassign off a row. One instance for the page; the row
			    it is editing is the target above. The save invalidates the
			    work-order caches, so the list refreshes itself. */}
			<AssignWorkOrderModal
				isOpen={assignTarget !== null}
				workOrder={assignTarget}
				onClose={() => setAssignTarget(null)}
			/>
			{deleteModal}
		</>
	);
};

export default WorkOrdersPage;
